import { Badge } from "@sylviq/ui-react-web/components/ui/badge";
import { PageHeader } from "@sylviq/ui-react-web/components/ui/page-header";
import { Tabs } from "@sylviq/ui-react-web/components/ui/tabs";
import { useNavigate } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import { reportLocationLabel } from "@/lib/api";
import { CultivationCapabilityNotice } from "./CultivationCapabilityNotice";
import { CultivationClose } from "./CultivationClose";
import { CultivationHistory } from "./CultivationHistory";
import { CultivationImports } from "./CultivationImports";
import { capabilityAllowed } from "./cultivation-capabilities";
import { CultivationOverview } from "./CultivationOverview";
import { dateLabel, monthLabel } from "./cultivation-dates";
import { closeStatus, evidenceCoverage, statusLabel, statusTone } from "./cultivation-readiness";
import type { CultivationPage, CultivationTab, CultivationWorkspace } from "./cultivation-types";
import { useCultivationWorkspace } from "./use-cultivation-workspace";

const PAGE_BY_TAB: Record<CultivationTab, CultivationPage> = {
  overview: "costing",
  imports: "imports",
  review: "close",
  history: "history",
};

const TAB_BY_PAGE: Record<CultivationPage, CultivationTab> = {
  costing: "overview",
  imports: "imports",
  close: "review",
  history: "history",
};

function reviewFor(workspace: CultivationWorkspace) {
  if (!workspace.installation) return null;
  return workspace.reviews.find((review) => review.installation_id === workspace.installation!.id) ?? null;
}

export function CultivationWorkspacePage({ page }: { page: CultivationPage }) {
  const navigate = useNavigate();
  const workspace = useCultivationWorkspace();
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [draftOverrides, setDraftOverrides] = useState<Record<string, string>>({});
  const [draftRevision, setDraftRevision] = useState(0);
  const workspaceScopeKey = [workspace.licenseId, workspace.periodStart, workspace.periodEnd].join("|");
  const [draftScopeKey, setDraftScopeKey] = useState(workspaceScopeKey);
  const visibleDraft = draftScopeKey === workspaceScopeKey ? draft : {};
  const visibleOverrides = draftScopeKey === workspaceScopeKey ? draftOverrides : {};
  const onDraftChange = useCallback((values: Record<string, string>) => {
    setDraft(values);
    setDraftScopeKey(workspaceScopeKey);
  }, [workspaceScopeKey]);

  useEffect(() => {
    setDraft({});
    setDraftOverrides({});
    setDraftScopeKey(workspaceScopeKey);
    setDraftRevision((current) => current + 1);
  }, [workspaceScopeKey]);
  const activeTab = TAB_BY_PAGE[page];
  const review = reviewFor(workspace);
  const coverage = evidenceCoverage(
    workspace.imports.imports,
    workspace.licenseId,
    workspace.periodStart,
    workspace.periodEnd,
    workspace.imports.attestations,
  );
  const status = closeStatus(review, coverage);
  const canView = capabilityAllowed(workspace.capabilities, "view");

  if (!canView) {
    return (
      <div className="grid min-w-0 gap-5">
        <PageHeader
          title="Cultivation costing"
          description="Import regulator reports, review the close inputs, then save the close for posting and locking."
        />
        <CultivationCapabilityNotice capability="view" action="open the cultivation workspace" />
      </div>
    );
  }

  function openTab(tab: CultivationTab) {
    const nextPage = PAGE_BY_TAB[tab];
    void navigate({
      to: "/extensions/$publisher/$key/$page",
      params: { publisher: "sylviq", key: "niche-cultivation-costing", page: nextPage },
      search: (previous: Record<string, unknown>) => previous,
      replace: true,
    });
  }

  return (
    <div className="grid min-w-0 gap-5">
      <PageHeader
        title="Cultivation costing"
        description="Import regulator reports, review the close inputs, then save the close for posting and locking."
        actions={<Badge tone={statusTone(status)}>{statusLabel(status)}</Badge>}
      />

      <section className="surface grid gap-3 rounded-2xl border border-[var(--border)] p-4 sm:grid-cols-2 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)_minmax(0,1fr)]">
        <label className="grid gap-1 text-xs font-medium">
          Location
          <select
            aria-label="Location"
            className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
            value={workspace.licenseId}
            onChange={(event) => workspace.setLicense(event.target.value)}
            disabled={workspace.locations.length === 0}
          >
            <option value="">Select a location</option>
            {workspace.locations.map((location) => (
              <option key={location.id} value={location.id}>
                {reportLocationLabel(location, workspace.locations)}
              </option>
            ))}
          </select>
        </label>
        <label className="grid gap-1 text-xs font-medium">
          Period start
          <input
            aria-label="Period start"
            type="date"
            className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
            value={workspace.periodStart}
            max={workspace.periodEnd}
            onChange={(event) => workspace.setPeriod(event.target.value, workspace.periodEnd)}
          />
        </label>
        <label className="grid gap-1 text-xs font-medium">
          Period end
          <input
            aria-label="Period end"
            type="date"
            className="min-h-10 rounded-md border border-[var(--border)] bg-[var(--surface)] px-3 text-sm"
            value={workspace.periodEnd}
            min={workspace.periodStart}
            onChange={(event) => workspace.setPeriod(workspace.periodStart, event.target.value)}
          />
        </label>
        <p className="text-xs text-[var(--muted-foreground)] sm:col-span-2 lg:col-span-3">
          {monthLabel(workspace.periodStart)}–{monthLabel(workspace.periodEnd)} · {dateLabel(workspace.periodStart)} to {dateLabel(workspace.periodEnd)}. The location and period are kept in the page address so a review can be linked or reloaded.
        </p>
      </section>

      {workspace.error ? <p role="alert" className="text-sm text-[var(--danger)]">{workspace.error}</p> : null}

      <Tabs
        tabs={[
          { value: "overview", label: "Overview" },
          { value: "imports", label: "Imports" },
          { value: "review", label: "Review" },
          { value: "history", label: "History" },
        ]}
        value={activeTab}
        onValueChange={(value) => openTab(value as CultivationTab)}
        label="Cultivation close"
      >
        {workspace.loading ? (
          <p role="status" className="text-sm text-[var(--muted-foreground)]">Loading cultivation workspace…</p>
        ) : activeTab === "overview" ? (
          <CultivationOverview
            workspace={workspace}
            onOpenImports={() => openTab("imports")}
            onOpenReview={() => openTab("review")}
          />
        ) : activeTab === "imports" ? (
          <CultivationImports workspace={workspace} onOpenReview={() => openTab("review")} />
        ) : activeTab === "history" ? (
          <CultivationHistory
            workspace={workspace}
            onOpenReview={() => openTab("review")}
            onOpenPeriodReview={(start, end) => {
              void navigate({
                to: "/extensions/$publisher/$key/$page",
                params: { publisher: "sylviq", key: "niche-cultivation-costing", page: "close" },
                search: (previous: Record<string, unknown>) => ({
                  ...previous,
                  period_start: start,
                  period_end: end,
                }),
                replace: true,
              });
            }}
          />
        ) : (
          <CultivationClose
            key={`${workspaceScopeKey}|${draftRevision}`}
            workspace={workspace}
            onOpenImports={() => openTab("imports")}
            initialValues={visibleDraft}
            draftOverrides={visibleOverrides}
            onDraftChange={onDraftChange}
            draft={visibleDraft}
          />
        )}
      </Tabs>
    </div>
  );
}
